import React, { useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {Button} from "semantic-ui-react";
import ItemsList from "./ItemsList";
import './styles.css';

const Transfer = ({
    items: propItems,
    itemKeySelector,
    itemRender,
    targetKeys: propTargetKeys,
    hasSearch,
    searchSelector,
    hasSelectAll,
    disabled,
    onTargetKeysChange,
}) => {
    const [items, setItems] = useState([]);
    const [targetKeys, setTargetKeys] = useState({});
    const [checkedKeys, setCheckedKeys] = useState({});

    useEffect(() => {
        setItems(propItems);
    }, [propItems]);

    useEffect(() => {
        setTargetKeys(propTargetKeys.reduce((acc, key) => {
            acc[key] = true;
            return acc;
        }, {}));
    }, [propTargetKeys]);

    const handleItemChecked = useCallback((key) => {
        setCheckedKeys((prevCheckedKeys) => {
            const newCheckedKeys = {...prevCheckedKeys};
            if (!newCheckedKeys[key]) {
                newCheckedKeys[key] = true;
            } else {
                delete newCheckedKeys[key];
            }
            return newCheckedKeys;
        });
    }, []);

    const setBatchCheckedKeys = useCallback((keys, checked) => {
        setCheckedKeys((prevCheckedKeys) => {
            const newCheckedKeys = {...prevCheckedKeys};
            keys.forEach((key) => {
                if (checked) {
                    newCheckedKeys[key] = true;
                } else {
                    delete newCheckedKeys[key];
                }
            });
            return newCheckedKeys;
        });
    }, []);

    const handleAllItemsChecked = useCallback((keys, checked) => {
        setBatchCheckedKeys(keys, checked);
    }, [setBatchCheckedKeys]);

    const setBatchTargetKeys = useCallback((keys, added) => {
        setTargetKeys((prevTargetKeys) => {
            const newTargetKeys = {...prevTargetKeys};
            keys.forEach((key) => {
                if (added) {
                    newTargetKeys[key] = true;
                } else {
                    delete newTargetKeys[key];
                }
            });
            onTargetKeysChange(Object.keys(newTargetKeys));
            return newTargetKeys;
        });
    }, [onTargetKeysChange]);

    const handleTransferCheckedSourceToTarget = useCallback(() => {
        const keysToAddToTarget = Object.keys(checkedKeys).filter((checkedKey) => !targetKeys[checkedKey]);
        setBatchTargetKeys(keysToAddToTarget, true);
        setBatchCheckedKeys(keysToAddToTarget, false);
    }, [checkedKeys, setBatchCheckedKeys, setBatchTargetKeys, targetKeys]);

    const handleTransferCheckedTargetToSource = useCallback(() => {
        const keysToRemoveFromTarget = Object.keys(checkedKeys).filter((checkedKey) => targetKeys[checkedKey]);
        setBatchTargetKeys(keysToRemoveFromTarget, false);
        setBatchCheckedKeys(keysToRemoveFromTarget, false);
    }, [checkedKeys, setBatchCheckedKeys, setBatchTargetKeys, targetKeys]);

    const internalItems = useMemo(() => {
        return items.map((item) => {
            const key = itemKeySelector(item);
            return {
                key,
                value: item,
                checked: !!checkedKeys[key],
                targeted: !!targetKeys[key],
            };
        });
    }, [itemKeySelector, items, checkedKeys, targetKeys]);

    const internalSourceItems = useMemo(() => {
        return internalItems.filter((item) => !item.targeted);
    }, [internalItems]);

    const internalTargetItems = useMemo(() => {
        return internalItems.filter((item) => item.targeted);
    }, [internalItems]);

    const hasSourceCheckedItems = useMemo(() => {
        return internalItems.some((item) => !item.targeted && item.checked);
    }, [internalItems]);

    const hasTargetCheckedItems = useMemo(() => {
        return internalItems.some((item) => item.targeted && item.checked);
    }, [internalItems]);

    return (
        <div className="transferContent">
            <div className="sourceItems">
                <ItemsList
                    key="source"
                    items={internalSourceItems}
                    onItemChecked={handleItemChecked}
                    itemRender={itemRender}
                    hasSelectAll={hasSelectAll}
                    onAllItemsChecked={handleAllItemsChecked}
                    hasSearch={hasSearch}
                    searchSelector={searchSelector}
                />
            </div>
            <div className="transferActions">
                <Button.Group
                    className="transferButtons"
                    vertical
                    icon
                    size="small"
                >
                    <div className="transferButton">
                        <Button
                            icon='angle right'
                            primary
                            inverted
                            onClick={handleTransferCheckedSourceToTarget}
                            disabled={disabled || !hasSourceCheckedItems}
                        />
                    </div>
                    <div className="transferButton">
                        <Button
                            icon='angle left'
                            primary
                            inverted
                            onClick={handleTransferCheckedTargetToSource}
                            disabled={disabled || !hasTargetCheckedItems}
                        />
                    </div>
                </Button.Group>
            </div>
            <div className="targetItems">
                <ItemsList
                    key="target"
                    items={internalTargetItems}
                    onItemChecked={handleItemChecked}
                    itemRender={itemRender}
                    hasSelectAll={hasSelectAll}
                    onAllItemsChecked={handleAllItemsChecked}
                    hasSearch={hasSearch}
                    searchSelector={searchSelector}
                    disabled={disabled}
                />
            </div>
        </div>
    );
}

Transfer.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    itemKeySelector: PropTypes.func,
    itemRender: PropTypes.func,
    targetKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    hasSearch: PropTypes.bool,
    searchSelector: PropTypes.func,
    hasSelectAll: PropTypes.bool,
    disabled: PropTypes.bool,
    onTargetKeysChange: PropTypes.func,
};

Transfer.defaultProps = {
    items: [],
    itemKeySelector: (item) => item.Id,
    itemRender: (item) => item.Id,
    targetKeys: [],
    hasSearch: false,
    searchSelector: () => false,
    hasSelectAll: true,
    disabled: false,
    onTargetKeysChange: () => {},
};

export default Transfer;